// Centralized SEO helper. Generates canonical URL, meta title/description,
// Open Graph, Twitter and Schema.org (JSON-LD) tags from a path, title and
// description.

export const SITE_URL = "https://hantavirus.international";
export const SITE_NAME = "Hantavirus International";
export const PUBLISHER = {
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.png` },
};

const TITLE_SUFFIX = " — Hantavirus International";
const MAX_TITLE = 60;
const MAX_DESC = 160;

export type JsonLd = Record<string, unknown>;

export type SeoInput = {
  /** Route path, e.g. "/", "/resources". Trailing slash is normalized. */
  path: string;
  /** Page-specific title. Suffix is appended automatically if missing. */
  title: string;
  /** Page-specific description (≤160 chars recommended). */
  description: string;
  /** Optional override OG image. Defaults to site-wide OG. */
  image?: string;
  /** Optional meta keywords for the page. */
  keywords?: string[];
  /** Optional og:type (default "website"; use "article" for posts). */
  type?: "website" | "article";
  /** Optional override canonical (defaults to SITE_URL + path). */
  canonical?: string;
  /** Optional human breadcrumb labels. Defaults to ["Home", titleized path]. */
  breadcrumb?: { name: string; path: string }[];
  /** Extra Schema.org nodes (Article, NewsArticle, MedicalWebPage, etc.). */
  jsonLd?: JsonLd | JsonLd[];
};

export type SeoHead = {
  meta: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
  scripts: Array<{ type: string; children: string }>;
};

function clamp(s: string, n: number) {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length <= n ? t : t.slice(0, n - 1).trimEnd() + "…";
}

function canonicalFromPath(path: string) {
  const p = path.startsWith("/") ? path : `/${path}`;
  const norm = p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
  return `${SITE_URL}${norm}`;
}

function withSuffix(title: string) {
  return title.includes("Hantavirus International") || title.length > MAX_TITLE
    ? clamp(title, MAX_TITLE)
    : clamp(title + TITLE_SUFFIX, MAX_TITLE);
}

function defaultBreadcrumb(path: string): { name: string; path: string }[] {
  const crumbs: { name: string; path: string }[] = [{ name: "Home", path: "/" }];
  if (path === "/" || path === "") return crumbs;
  const segs = path.split("/").filter(Boolean);
  let acc = "";
  for (const s of segs) {
    acc += `/${s}`;
    crumbs.push({
      name: s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      path: acc,
    });
  }
  return crumbs;
}

function buildBreadcrumb(items: { name: string; path: string }[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: canonicalFromPath(c.path),
    })),
  };
}

/**
 * Build a TanStack `head()` payload (meta + links + JSON-LD scripts) for a route.
 *
 * Example:
 *   head: () => buildSeo({
 *     path: "/resources",
 *     title: "Hantavirus Prevention Guide",
 *     description: "Evidence-based prevention protocols and variant reference.",
 *     jsonLd: { "@context": "https://schema.org", "@type": "MedicalWebPage", ... },
 *   }),
 */
export function buildSeo(input: SeoInput): SeoHead {
  const url = input.canonical ?? canonicalFromPath(input.path);
  const title = withSuffix(input.title);
  const description = clamp(input.description, MAX_DESC);
  const ogType = input.type ?? "website";

  // A small, page-agnostic core. Routes pass their own keywords; the core
  // just keeps the brand discoverable. No "hanta virus" (split spelling)
  // because the canonical title uses the joined form.
  const CORE_KEYWORDS = ["hantavirus", "hantavirus international"];
  const keywords = input.keywords
    ? [...new Set([...input.keywords, ...CORE_KEYWORDS])]
    : CORE_KEYWORDS;

  const breadcrumb = buildBreadcrumb(input.breadcrumb ?? defaultBreadcrumb(input.path));
  const extraLd = input.jsonLd
    ? Array.isArray(input.jsonLd)
      ? input.jsonLd
      : [input.jsonLd]
    : [];

  const scripts = [breadcrumb, ...extraLd].map((node) => ({
    type: "application/ld+json",
    children: JSON.stringify(node),
  }));

  // Resolve image to absolute URL when provided.
  const image = input.image
    ? input.image.startsWith("http")
      ? input.image
      : `${SITE_URL}${input.image.startsWith("/") ? "" : "/"}${input.image}`
    : undefined;

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { name: "keywords", content: keywords.join(", ") },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:type", content: ogType },
    { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
  ];

  if (image) {
    meta.push(
      { property: "og:image", content: image },
      { property: "og:image:width", content: "1216" },
      { property: "og:image:height", content: "640" },
      { name: "twitter:image", content: image },
    );
  }

  return {
    meta,
    links: [{ rel: "canonical", href: url }],
    scripts,
  };
}

/** Helper: NewsArticle node for outbreak case items. */
export function newsArticle(opts: {
  url: string;
  headline: string;
  description: string;
  datePublished: string;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    mainEntityOfPage: { "@type": "WebPage", "@id": opts.url },
    headline: clamp(opts.headline, 110),
    description: clamp(opts.description, MAX_DESC),
    datePublished: opts.datePublished,
    dateModified: opts.datePublished,
    author: PUBLISHER,
    publisher: PUBLISHER,
  };
}
